import { Controller, UseFormReturn } from "react-hook-form";
import { formatDateToString } from "../../../utils/formatDateToString";
import Button from "../../Button";
import EditableContentBlock from "../../EditableContentBlock";
import FormDateInput from "../../FormDateInput";
import FormDropdown, { FormDropdownOption } from "../../FormDropdown";
import FormTextInput from "../../FormTextInput";
import {
    EditDvlaFormValues,
    EditDvlaLicenceInformationFormValues,
} from "../EditDvlaFormValues";
import licenceSample from "../../../licence-sample.png";
import { useWatch } from "react-hook-form";
import { GREAT_BRITAIN, NORTHERN_IRELAND, countryList } from "../../../constants/countries";
import {
    licenceStatusOptions,
    licenceTypeOptions,
} from "../../../constants/dvlaConstants";
import { LicenceStatus } from "../../../models/shared/statuses";
import { DvlaLicenceDto } from "../../../models/app/admin/dvla";

export type EditLicenceInformationFormProps = {
    initialValues: EditDvlaFormValues;
    isManual: boolean;
    methods: UseFormReturn<EditDvlaLicenceInformationFormValues>;
    isLoading: boolean;
    onSubmit: (
        data: EditDvlaLicenceInformationFormValues,
        callback: () => void
    ) => Promise<void>;
    canEdit: boolean;
};

const countryOfOriginOptions = countryList.map((country) => ({
    label: country.name,
    value: country.name,
}));

const licenceCountryOnChange  = (
    value: string, 
    option: FormDropdownOption<string>, 
    handle: (value: string, option: FormDropdownOption<string>) => void, 
    methods: UseFormReturn<DvlaLicenceDto>
    ) => {
        methods.setValue("licenceStatus", null);
        if(value !== GREAT_BRITAIN && value !== NORTHERN_IRELAND){
            methods.resetField("licenceType");
            methods.setValue("licenceType", null);
            methods.setValue("licenceIssueNumber", "");
        } 
        handle(value, option);
}

const EditLicenceInformationForm: React.FC<EditLicenceInformationFormProps> = ({
    initialValues,
    isManual,
    methods,
    isLoading,
    onSubmit,
    canEdit,
}) => {
    const control = methods.control;
    const countryOfOrigin = useWatch({ control, name: "licenceCountryOrigin" });
    const licenceStatus = useWatch({ control, name: "licenceStatus" });
    const isForeignSelected = countryOfOrigin !== GREAT_BRITAIN && countryOfOrigin !== NORTHERN_IRELAND;

    return (
        <EditableContentBlock
        onOpen={
            () => {
                //protect against changing from previously checked UK or NI Licence (since licence status likely to be filled out)
                if(isForeignSelected && licenceStatus != "No Adverse DVLA Record") {
                    methods.setValue("licenceStatus", null);
                    methods.setValue("licenceType", null);
                }
            }
        }
            title="Licence information"
            disableEdit={!canEdit}
        >
            {({ isEdit, setIsEdit }) => (
                <>
                    {!isEdit && (
                        <div className="space-y-2 p-6">
                            <p>
                                <b>Country of origin:</b>{" "}
                                {initialValues.licenceCountryOrigin}
                            </p>
                            <p>
                                <b>Licence number:</b>{" "}
                                {initialValues.licenceNumber}
                            </p>
                            <p>
                                <b>Licence status:</b>{" "}
                                {initialValues.licenceStatus}
                            </p>
                            <p>
                                <b>Licence type:</b> {initialValues.licenceType}
                            </p>
                            <p>
                                <b>Valid from:</b>{" "}
                                {initialValues.licenceValidFrom instanceof Date
                                    ? formatDateToString(
                                          initialValues.licenceValidFrom
                                      )
                                    : initialValues.licenceValidFrom &&
                                      formatDateToString(
                                          new Date(
                                              initialValues.licenceValidFrom
                                          )
                                      )}
                            </p>
                            <p>
                                <b>Valid to:</b>{" "}
                                {initialValues.licenceValidTo instanceof Date
                                    ? formatDateToString(
                                          initialValues.licenceValidTo
                                      )
                                    : initialValues.licenceValidTo &&
                                      formatDateToString(
                                          new Date(initialValues.licenceValidTo)
                                      )}
                            </p>
                            <p>
                                <b>Issue number:</b>{" "}
                                {initialValues.licenceIssueNumber}
                            </p>
                        </div>
                    )}
                    {isEdit && (
                        <div className="p-6">
                            <form
                                onSubmit={methods.handleSubmit(
                                    (
                                        data: EditDvlaLicenceInformationFormValues
                                    ) => onSubmit(data, () => setIsEdit(false))
                                )}
                            >
                                <Controller
                                    name="licenceCountryOrigin"
                                    control={methods.control}
                                    render={({
                                        field: { onChange, value },
                                    }) => (
                                        <FormDropdown
                                            label="Driving licence country of origin"
                                            options={countryOfOriginOptions}
                                            value={value}
                                            onChange={
                                                (value, option) => {
                                                    licenceCountryOnChange(value, option, onChange, methods);
                                                }
                                            }
                                            pleaseSelectText="Select driving licence country of origin"
                                        />
                                    )}
                                />
                                <FormTextInput
                                    label="Licence Number"
                                    required
                                    id="licence-number"
                                    placeholder="Enter licence number"
                                    error={
                                        methods.formState.errors.licenceNumber
                                    }
                                    helpText={
                                        countryOfOrigin === GREAT_BRITAIN
                                            ? "Licence number must be 16 characters with no spaces"
                                            : undefined
                                    }
                                    {...methods.register("licenceNumber")}
                                    moreInfo={
                                        countryOfOrigin === GREAT_BRITAIN
                                            ? {
                                                  dialogPrompt:
                                                      "Where is my licence number?",
                                                  description:
                                                      "Your licence number can be found on your UK Driving Licence at the position illustrated below, this will be 16 characters with no spaces.",
                                                  content: (
                                                      <img
                                                          className="w-full max-w-xl"
                                                          src={licenceSample}
                                                          alt="licence sample MORGA753116SM9IJ"
                                                      />
                                                  ),
                                              }
                                            : undefined
                                    }
                                    autoFocus
                                />
                                <Controller
                                    name="licenceStatus"
                                    control={methods.control}
                                    render={({
                                        field: { onChange, value },
                                    }) => (
                                        <FormDropdown
                                            required
                                            error={methods.formState.errors.licenceStatus}
                                            label="Licence status"
                                            options={
                                                isForeignSelected ? 
                                                    licenceStatusOptions.filter((e) => e.value == LicenceStatus.NoAdverseDVLARecord) :
                                                    licenceStatusOptions.filter((e) => e.value != LicenceStatus.NoAdverseDVLARecord)
                                                }
                                            value={value}
                                            onChange={onChange}
                                            pleaseSelectText="Select licence status"
                                        />
                                    )}
                                />
                                <Controller
                                    name="licenceType"
                                    control={methods.control}
                                    render={({
                                        field: { onChange, value },
                                    }) => (
                                        <FormDropdown
                                            required={!isForeignSelected}
                                            error={methods.formState.errors.licenceType}
                                            label="Licence type"
                                            options={licenceTypeOptions}
                                            value={value}
                                            onChange={onChange}
                                            pleaseSelectText="Select licence type"
                                        />
                                    )}
                                />
                                <Controller
                                    name="licenceValidFrom"
                                    control={methods.control}
                                    render={({
                                        field: { onChange, value },
                                    }) => (
                                        <FormDateInput
                                            required={!isForeignSelected}
                                            id="licenceValidFrom"
                                            label="Valid from"
                                            value={value}
                                            onChange={onChange}
                                            error={
                                                methods.formState.errors
                                                    .licenceValidFrom
                                            }
                                        />
                                    )}
                                />
                                <Controller
                                    name="licenceValidTo"
                                    control={methods.control}
                                    render={({
                                        field: { onChange, value },
                                    }) => (
                                        <FormDateInput
                                            required={!isForeignSelected}
                                            id="licenceValidTo"
                                            label="Valid to"
                                            value={value}
                                            onChange={onChange}
                                            error={
                                                methods.formState.errors
                                                    .licenceValidTo
                                            }
                                        />
                                    )}
                                />
                                <FormTextInput
                                    required={!isForeignSelected}
                                    label="Issue number"
                                    defaultValue={
                                        initialValues.licenceIssueNumber
                                    }
                                    id="licenseIssueNumber"
                                    placeholder="Enter licence issue number"
                                    error={
                                        methods.formState.errors
                                            .licenceIssueNumber
                                    }
                                    {...methods.register("licenceIssueNumber")}
                                />
                                <div className="mt-8 flex justify-between">
                                    <Button
                                        variant="tertiary"
                                        type="button"
                                        onClick={() => {
                                            methods.reset();
                                            setIsEdit(false);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="submit" loading={isLoading}>
                                        Save
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}
                </>
            )}
        </EditableContentBlock>
    );
};

export default EditLicenceInformationForm;
